// El PUENTE entre el chat y la pantalla que el usuario tiene abierta —
// ADR-2026-09-10-el-agente-toca-la-pantalla.
//
// Una pantalla con campos (el modo Modelo, Fotos de producto, Animar imagen…) registra
// un CONTROLADOR mientras está montada: sabe DESCRIBIRSE (qué campos tiene, qué hay
// puesto en cada uno, qué opciones acepta) y APLICAR cambios. El chat le ofrece al
// agente tres herramientas encima de eso —`ver_pantalla`, `configurar_pantalla`,
// `abrir_modo`— y con ellas puede explicar cada campo y dejarle la pantalla configurada
// al usuario, que es quien toca Generar.
//
// LA REGLA QUE HACE QUE NO SE PUEDA ROMPER NADA: el agente nunca escribe en el estado de
// la pantalla; manda (campo, valor) y acá se valida contra la descripción que la MISMA
// pantalla acaba de dar —una opción tiene que estar en su lista, un número dentro de su
// rango, un texto dentro de su largo, un campo tiene que existir y ser editable—. Lo que
// no pasa se rechaza con un motivo que el modelo puede leer y explicar; lo que pasa lo
// aplica la pantalla con sus propios setters, o sea exactamente lo que haría un click.
// Y como las opciones salen de las mismas listas que dibujan la interfaz, un valor que la
// interfaz no ofrece no entra por acá.
//
// Una pantalla de TRABAJO (el Estudio de Video) no tiene campos sino HERRAMIENTAS
// propias —cortar, mover, cargar una escena IA, transcribir— y las registra por el mismo
// puente (ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente): viajan al
// modelo sólo mientras esa pantalla está montada, y se ejecutan con el mismo executor que
// usaba el Director. Fuera del editor no existe ninguna.
//
// Todo puro y testeable. Nada de React acá; el registro vive en
// `PuenteDePantallaContext.tsx`.

import type { HerramientaChat } from './tipos';

export type TipoDeCampo = 'opcion' | 'opciones' | 'texto' | 'numero' | 'booleano';

export interface OpcionDeCampo {
  /** Lo que el agente manda en `configurar_pantalla`. */
  id: string;
  /** Cómo la ve el usuario en pantalla. */
  etiqueta: string;
  /** Un dato corto que ayude a elegir ("2 personas", "1 crédito/imagen"). */
  nota?: string;
}

export type ValorDeCampo = string | string[] | number | boolean | null;

export interface CampoDePantalla {
  /** Estable y en snake_case: es la clave que el agente usa. */
  id: string;
  /** El nombre con el que el usuario lo ve en pantalla. */
  etiqueta: string;
  /** Para qué sirve, en una o dos frases, como se lo explicarías al usuario. */
  queEs: string;
  tipo: TipoDeCampo;
  /** Valor actual: id (o ids) para opcion/opciones; texto; número; booleano. */
  valor: ValorDeCampo;
  /** El valor actual dicho en criollo ("Automático", "2 poses", "(vacío)"). */
  valorLegible: string;
  /** Lista cerrada para opcion/opciones. Es también la lista contra la que se valida. */
  opciones?: OpcionDeCampo[];
  /**
   * Si la lista ya está en el prompt (los productos del catálogo) no se repite en la
   * ficha: acá va el texto que dice dónde encontrarla. La validación usa `opciones` igual.
   */
  opcionesEnElPrompt?: string;
  /** opciones: tope de elegidos · texto: largo máximo · numero: máximo. */
  max?: number;
  /** opciones: mínimo de elegidos · numero: mínimo. */
  min?: number;
  /** numero: sólo enteros. */
  entero?: boolean;
  /** false = se muestra pero no se toca desde el chat (se elige en pantalla). */
  editable: boolean;
  motivoNoEditable?: string;
  /** Un consejo corto para el agente al tocarlo. */
  nota?: string;
}

export interface DescripcionDePantalla {
  /** "el modo Modelo", "Animar imagen", "la plantilla «Verano»". */
  pantalla: string;
  ruta: string;
  /** Qué hace la pantalla, en una línea para el usuario. */
  queHace: string;
  /** En qué paso está ("elegir el producto", "configurar"). */
  paso?: string;
  /** En el orden en que aparecen en la pantalla. */
  campos: CampoDePantalla[];
  /** Qué falta para poder generar. Vacío = listo, sólo falta el click del usuario. */
  faltaParaGenerar: string[];
  /** Cuánto costaría generar con lo que está puesto, si se sabe. */
  costo?: string;
  /** Cosas que el agente tiene que saber de esta pantalla y no son un campo. */
  notas?: string[];
  /**
   * Para las pantallas de TRABAJO (el editor de video): el estado en una o dos líneas
   * —proyecto, cuántos clips, qué hay seleccionado, qué panel está abierto—. Reemplaza al
   * resumen de campos en el bloque volátil del prompt.
   */
  estado?: string;
}

export interface CambioDeCampo {
  campo: string;
  valor: unknown;
}

/** Un cambio que pasó la validación: el valor ya está en el tipo del campo. */
export interface CambioValidado {
  campo: string;
  valor: Exclude<ValorDeCampo, null>;
  etiqueta: string;
  /** Cómo contarle al usuario lo que quedó ("Modelo: Lucía"). */
  legible: string;
}

export interface CambioRechazado {
  campo: string;
  motivo: string;
}

export interface AplicacionDeCambios {
  /** Ids de los campos que la pantalla aplicó. */
  aplicados: string[];
  /** Los que la pantalla no pudo aplicar aun validados (raro: el estado cambió). */
  fallidos: CambioRechazado[];
}

/**
 * Una herramienta PROPIA de una pantalla. La `etiqueta` es lo que el chat muestra mientras
 * corre ("Cortando el clip…"), porque estas herramientas no tienen tarjeta.
 */
export interface HerramientaDePantalla extends HerramientaChat {
  etiqueta: string;
}

/** Lo que devuelve `ejecutar`: el contrato de un `tool_result` de la API. */
export interface ResultadoDeHerramientaDePantalla {
  /** Texto (o JSON serializado), o bloques de contenido (texto + imágenes). */
  content: string | unknown[];
  is_error?: boolean;
}

export interface ControladorDePantalla {
  /**
   * Identifica la pantalla ("modo:design-modelo", "animar-imagen", "editor-video").
   * `abrir_modo` espera a que aparezca la clave del modo que abrió antes de seguir.
   */
  clave: string;
  describir: () => DescripcionDePantalla;
  aplicar: (cambios: CambioValidado[]) => Promise<AplicacionDeCambios> | AplicacionDeCambios;
  /**
   * Herramientas propias de la pantalla, si las tiene. Se suman a las del chat sólo
   * mientras la pantalla está montada, y el chat las resuelve llamando a `ejecutar` con
   * el mismo nombre e input. Sin tarjeta: el modelo recibe el resultado y sigue.
   */
  herramientas?: HerramientaDePantalla[];
  ejecutar?: (nombre: string, input: unknown) => Promise<ResultadoDeHerramientaDePantalla>;
  /** La skill que explica CÓMO trabajar en esta pantalla; el prompt se la sugiere. */
  skill?: string;
}

// ── Validación ───────────────────────────────────────────────────────────────

/** Largo máximo de un texto cuando el campo no dice otro. */
export const LARGO_TEXTO_POR_DEFECTO = 1000;

const normalizar = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

/** Resuelve un id o una etiqueta (sin acentos ni mayúsculas) contra la lista. */
function resolverOpcion(campo: CampoDePantalla, crudo: unknown): OpcionDeCampo | null {
  if (typeof crudo !== 'string' && typeof crudo !== 'number') return null;
  const buscado = normalizar(String(crudo));
  if (!buscado) return null;
  const opciones = campo.opciones ?? [];
  return (
    opciones.find((o) => o.id === String(crudo)) ??
    opciones.find((o) => normalizar(o.id) === buscado) ??
    opciones.find((o) => normalizar(o.etiqueta) === buscado) ??
    null
  );
}

function listaDeOpciones(campo: CampoDePantalla): string {
  if (campo.opcionesEnElPrompt) return campo.opcionesEnElPrompt;
  const ids = (campo.opciones ?? []).map((o) => o.id);
  return ids.length > 0 ? ids.slice(0, 12).join(', ') + (ids.length > 12 ? ', …' : '') : '(ninguna)';
}

function validarUno(
  campo: CampoDePantalla,
  crudo: unknown,
): { ok: true; cambio: CambioValidado } | { ok: false; motivo: string } {
  const base = { campo: campo.id, etiqueta: campo.etiqueta };
  switch (campo.tipo) {
    case 'opcion': {
      const op = resolverOpcion(campo, crudo);
      if (!op) {
        return {
          ok: false,
          motivo: `"${String(crudo)}" no es una opción de ${campo.etiqueta}. Válidas: ${listaDeOpciones(campo)}.`,
        };
      }
      return { ok: true, cambio: { ...base, valor: op.id, legible: `${campo.etiqueta}: ${op.etiqueta}` } };
    }
    case 'opciones': {
      const lista = Array.isArray(crudo) ? crudo : crudo === null || crudo === undefined ? [] : [crudo];
      const resueltas: OpcionDeCampo[] = [];
      for (const item of lista) {
        const op = resolverOpcion(campo, item);
        if (!op) {
          return {
            ok: false,
            motivo: `"${String(item)}" no es una opción de ${campo.etiqueta}. Válidas: ${listaDeOpciones(campo)}.`,
          };
        }
        if (!resueltas.some((r) => r.id === op.id)) resueltas.push(op);
      }
      const min = campo.min ?? 0;
      if (resueltas.length < min) {
        return { ok: false, motivo: `${campo.etiqueta} necesita al menos ${min}.` };
      }
      if (campo.max !== undefined && resueltas.length > campo.max) {
        return {
          ok: false,
          motivo: `${campo.etiqueta} admite hasta ${campo.max} (mandaste ${resueltas.length}).`,
        };
      }
      const legible =
        resueltas.length === 0
          ? `${campo.etiqueta}: ninguno`
          : `${campo.etiqueta}: ${resueltas.map((r) => r.etiqueta).join(', ')}`;
      return { ok: true, cambio: { ...base, valor: resueltas.map((r) => r.id), legible } };
    }
    case 'texto': {
      if (crudo === null || crudo === undefined) crudo = '';
      if (typeof crudo !== 'string') {
        return { ok: false, motivo: `${campo.etiqueta} espera un texto.` };
      }
      const texto = crudo.trim();
      const max = campo.max ?? LARGO_TEXTO_POR_DEFECTO;
      if (texto.length > max) {
        return {
          ok: false,
          motivo: `${campo.etiqueta} admite hasta ${max} caracteres (mandaste ${texto.length}). Acortalo.`,
        };
      }
      const legible = texto ? `${campo.etiqueta}: "${texto.length > 80 ? `${texto.slice(0, 77)}…` : texto}"` : `${campo.etiqueta}: (vacío)`;
      return { ok: true, cambio: { ...base, valor: texto, legible } };
    }
    case 'numero': {
      const n = typeof crudo === 'number' ? crudo : typeof crudo === 'string' ? Number(crudo.trim()) : NaN;
      if (!Number.isFinite(n)) {
        return { ok: false, motivo: `${campo.etiqueta} espera un número.` };
      }
      if (campo.entero && !Number.isInteger(n)) {
        return { ok: false, motivo: `${campo.etiqueta} tiene que ser un entero.` };
      }
      if (campo.min !== undefined && n < campo.min) {
        return { ok: false, motivo: `${campo.etiqueta} va de ${campo.min} a ${campo.max ?? '∞'} (mandaste ${n}).` };
      }
      if (campo.max !== undefined && n > campo.max) {
        return { ok: false, motivo: `${campo.etiqueta} va de ${campo.min ?? 0} a ${campo.max} (mandaste ${n}).` };
      }
      return { ok: true, cambio: { ...base, valor: n, legible: `${campo.etiqueta}: ${n}` } };
    }
    case 'booleano': {
      let b: boolean | null = null;
      if (typeof crudo === 'boolean') b = crudo;
      else if (typeof crudo === 'string') {
        const s = normalizar(crudo);
        if (['true', 'si', 'sí', 'on', '1', 'prendido', 'activado'].includes(s)) b = true;
        if (['false', 'no', 'off', '0', 'apagado', 'desactivado'].includes(s)) b = false;
      } else if (typeof crudo === 'number') b = crudo !== 0;
      if (b === null) {
        return { ok: false, motivo: `${campo.etiqueta} espera true o false.` };
      }
      return { ok: true, cambio: { ...base, valor: b, legible: `${campo.etiqueta}: ${b ? 'sí' : 'no'}` } };
    }
  }
}

/**
 * Valida una lista de cambios contra la descripción vigente de la pantalla. Un campo
 * repetido se queda con el último valor. Lo que no pasa se rechaza con motivo; lo demás
 * sale tipado y listo para que la pantalla lo aplique.
 */
export function validarCambios(
  descripcion: DescripcionDePantalla,
  cambios: CambioDeCampo[],
): { validos: CambioValidado[]; rechazados: CambioRechazado[] } {
  const validos: CambioValidado[] = [];
  const rechazados: CambioRechazado[] = [];
  const porId = new Map(descripcion.campos.map((c) => [c.id, c]));
  const idsDePantalla =
    descripcion.campos.length > 0
      ? descripcion.campos.map((c) => c.id).join(', ')
      : '(ninguno: esta pantalla no tiene campos; usá sus herramientas propias)';

  for (const cambio of cambios) {
    const id = typeof cambio.campo === 'string' ? cambio.campo.trim() : '';
    const campo =
      porId.get(id) ??
      descripcion.campos.find((c) => normalizar(c.id) === normalizar(id)) ??
      descripcion.campos.find((c) => normalizar(c.etiqueta) === normalizar(id));
    if (!campo) {
      rechazados.push({
        campo: id || '(sin campo)',
        motivo: `No hay un campo "${id}" en esta pantalla. Los campos son: ${idsDePantalla}.`,
      });
      continue;
    }
    if (!campo.editable) {
      rechazados.push({
        campo: campo.id,
        motivo: `${campo.etiqueta} no se puede cambiar desde el chat${campo.motivoNoEditable ? `: ${campo.motivoNoEditable}` : '.'}`,
      });
      continue;
    }
    const r = validarUno(campo, cambio.valor);
    if (!r.ok) {
      rechazados.push({ campo: campo.id, motivo: r.motivo });
      continue;
    }
    const previo = validos.findIndex((v) => v.campo === campo.id);
    if (previo >= 0) validos.splice(previo, 1);
    validos.push(r.cambio);
  }
  return { validos, rechazados };
}

/** Lee el input crudo de `configurar_pantalla` (lo escribe el modelo: puede venir mal). */
export function normalizarCambios(input: unknown): CambioDeCampo[] {
  if (!input || typeof input !== 'object') return [];
  const lista = (input as { cambios?: unknown }).cambios;
  if (!Array.isArray(lista)) return [];
  return lista
    .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
    .map((c) => ({ campo: typeof c.campo === 'string' ? c.campo : '', valor: c.valor }));
}

// ── Cómo se le cuenta la pantalla al modelo ──────────────────────────────────

const TIPO_HUMANO: Record<TipoDeCampo, string> = {
  opcion: 'una opción',
  opciones: 'varias opciones',
  texto: 'texto',
  numero: 'número',
  booleano: 'sí/no',
};

/** Cuántas opciones se listan enteras en la ficha antes de resumir. */
const TOPE_OPCIONES_EN_FICHA = 60;

function lineaDeCampo(c: CampoDePantalla, i: number): string {
  const partes: string[] = [];
  let tipo = TIPO_HUMANO[c.tipo];
  if (c.tipo === 'opciones' && c.max !== undefined) tipo += ` (hasta ${c.max})`;
  if (c.tipo === 'texto' && c.max !== undefined) tipo += ` (hasta ${c.max} caracteres)`;
  if (c.tipo === 'numero') tipo += ` (${c.min ?? 0} a ${c.max ?? '∞'}${c.entero ? ', entero' : ''})`;
  partes.push(`${i + 1}. ${c.id} — "${c.etiqueta}" [${tipo}]: ${c.queEs}`);
  partes.push(`   Ahora: ${c.valorLegible}`);
  if (!c.editable) {
    partes.push(`   SOLO LECTURA: ${c.motivoNoEditable ?? 'se elige en pantalla.'}`);
  } else if (c.opcionesEnElPrompt) {
    partes.push(`   Opciones: ${c.opcionesEnElPrompt}`);
  } else if (c.opciones && c.opciones.length > 0) {
    const visibles = c.opciones.slice(0, TOPE_OPCIONES_EN_FICHA);
    const resto = c.opciones.length - visibles.length;
    partes.push(
      `   Opciones (id = etiqueta): ${visibles
        .map((o) => `${o.id} = ${o.etiqueta}${o.nota ? ` (${o.nota})` : ''}`)
        .join(' · ')}${resto > 0 ? ` · y ${resto} más` : ''}`,
    );
  }
  if (c.nota) partes.push(`   Nota: ${c.nota}`);
  return partes.join('\n');
}

/** La primera oración de una descripción de herramienta, para listarla en la ficha. */
function primeraFrase(texto: string, max = 160): string {
  const corte = texto.indexOf('. ');
  const frase = (corte > 0 ? texto.slice(0, corte + 1) : texto).trim();
  return frase.length > max ? `${frase.slice(0, max - 1)}…` : frase;
}

/**
 * La ficha completa, para `ver_pantalla`. `herramientas` son las propias de la pantalla
 * (si las registró): se listan para que el modelo sepa qué puede hacer acá.
 */
export function textoDeDescripcion(
  d: DescripcionDePantalla,
  herramientas: Array<{ name: string; description: string }> = [],
): string {
  const lineas: string[] = [];
  lineas.push(`PANTALLA: ${d.pantalla} · ruta ${d.ruta}${d.paso ? ` · paso: ${d.paso}` : ''}`);
  lineas.push(`QUÉ HACE: ${d.queHace}`);
  if (d.estado) lineas.push(`ESTADO: ${d.estado}`);
  if (d.campos.length === 0) {
    lineas.push(
      herramientas.length > 0
        ? 'CAMPOS: no tiene campos para configurar_pantalla; acá se trabaja con las herramientas propias de la pantalla (abajo).'
        : 'CAMPOS: esta pantalla no tiene campos que puedas tocar desde el chat.',
    );
  } else {
    lineas.push('CAMPOS (en el orden de la pantalla; el id es lo que mandás en configurar_pantalla):');
    d.campos.forEach((c, i) => lineas.push(lineaDeCampo(c, i)));
  }
  // Una pantalla de trabajo sin campos no tiene "Generar": la línea sobra.
  if (d.campos.length > 0 || d.faltaParaGenerar.length > 0) {
    lineas.push(
      d.faltaParaGenerar.length === 0
        ? 'PARA GENERAR: está todo; falta que el USUARIO toque Generar (vos no podés).'
        : `PARA GENERAR FALTA: ${d.faltaParaGenerar.join('; ')}.`,
    );
  }
  if (d.costo) lineas.push(`COSTO: ${d.costo}`);
  if (d.notas && d.notas.length > 0) lineas.push(`NOTAS: ${d.notas.join(' ')}`);
  if (herramientas.length > 0) {
    lineas.push(
      `HERRAMIENTAS PROPIAS DE ESTA PANTALLA (ya están entre tus tools; existen sólo mientras el usuario está acá):\n${herramientas
        .map((h) => `• ${h.name} — ${primeraFrase(h.description)}`)
        .join('\n')}`,
    );
  }
  return lineas.join('\n');
}

/** Largo máximo del resumen que va en cada turno del prompt. */
export const LARGO_RESUMEN_PANTALLA = 600;

/** Una o dos líneas para el bloque volátil del prompt: dónde está y qué tiene puesto. */
export function resumenDePantalla(d: DescripcionDePantalla): string {
  if (d.campos.length === 0 && d.estado) {
    const texto = `${d.pantalla}. ${d.estado}`;
    return texto.length > LARGO_RESUMEN_PANTALLA ? `${texto.slice(0, LARGO_RESUMEN_PANTALLA - 1)}…` : texto;
  }
  const elegido = d.campos.map((c) => `${c.etiqueta}: ${c.valorLegible}`).join(' · ');
  const estado =
    d.faltaParaGenerar.length === 0
      ? 'Para generar: listo (le falta tocar Generar).'
      : `Para generar falta: ${d.faltaParaGenerar.join('; ')}.`;
  const texto = `${d.pantalla}${d.paso ? ` (paso: ${d.paso})` : ''}. ${
    elegido ? `Tiene puesto — ${elegido}. ` : ''
  }${estado}`;
  return texto.length > LARGO_RESUMEN_PANTALLA ? `${texto.slice(0, LARGO_RESUMEN_PANTALLA - 1)}…` : texto;
}

// ── Los tool_result ──────────────────────────────────────────────────────────

/** `ver_pantalla`: la ficha, o el aviso de que acá no hay campos. */
export function resultadoDeVerPantalla(
  descripcion: DescripcionDePantalla | null,
  pantallaPorRuta: string,
  herramientas: Array<{ name: string; description: string }> = [],
): Record<string, unknown> {
  if (!descripcion) {
    return {
      hay_campos: false,
      pantalla: pantallaPorRuta,
      texto: `El usuario está en ${pantallaPorRuta}. Esta pantalla no tiene campos que puedas tocar desde el chat: ayudalo explicando, o llevalo a un modo con abrir_modo si lo que quiere hacer es generar algo.`,
    };
  }
  return {
    hay_campos: descripcion.campos.length > 0,
    hay_herramientas: herramientas.length > 0,
    texto: textoDeDescripcion(descripcion, herramientas),
  };
}

/** `configurar_pantalla`: qué quedó, qué no y por qué, y cómo quedó la pantalla. */
export function resultadoDeConfigurar(a: {
  aplicados: CambioValidado[];
  rechazados: CambioRechazado[];
  fallidos: CambioRechazado[];
  despues: DescripcionDePantalla | null;
}): Record<string, unknown> {
  const rechazados = [...a.rechazados, ...a.fallidos];
  const lineas: string[] = [];
  if (a.aplicados.length > 0) {
    lineas.push(`APLICADO: ${a.aplicados.map((c) => c.legible).join(' · ')}.`);
  }
  if (rechazados.length > 0) {
    lineas.push(`NO SE APLICÓ: ${rechazados.map((r) => `${r.campo} — ${r.motivo}`).join(' | ')}`);
  }
  if (a.aplicados.length === 0 && rechazados.length === 0) {
    lineas.push('No llegó ningún cambio válido.');
  }
  if (a.despues) lineas.push(`PANTALLA AHORA: ${resumenDePantalla(a.despues)}`);
  lineas.push(
    'El usuario ve los cambios en pantalla. Contale en una línea qué quedó y que toque Generar cuando quiera; no digas que generaste.',
  );
  return {
    aplicados: a.aplicados.map((c) => c.campo),
    rechazados,
    texto: lineas.join('\n'),
  };
}

/** Sin pantalla registrada, `configurar_pantalla` no tiene dónde escribir. */
export function resultadoSinPantalla(pantallaPorRuta: string): Record<string, unknown> {
  return {
    aplicados: [],
    rechazados: [],
    texto: `El usuario está en ${pantallaPorRuta}, que no tiene campos para configurar. Si quiere generar algo, llevalo a un modo con abrir_modo (avisándole) y configurá ahí.`,
  };
}
